import {
  Button,
  ButtonColor,
  formatName,
  formatPrice,
  formatTimeFromMinutes,
  humanizeTier,
  IconChevronDown,
  Modal,
  ModalContent,
  type ModalHook,
  ModalSize,
  ProfileImage,
  useControlledModalState,
} from '@leland-dev/leland-ui-library';
import React, { type FC, useState } from 'react';

import {
  type GoalName,
  type OfferingPackagePrice,
} from '../../__generated-gql-types__/globalTypes';
import { useCountLines } from '../../utils/hooks/useCountLines';
import { getOfferingPackageCoachingTimeLabel } from '../../utils/package';
import { getTierDescription } from '../../utils/tier';
import SvgIcon, { Svg } from '../SvgIcon';

import {
  type SelectATierModalOfferingPackageFragment,
  type SelectATierModalOfferingPackagePriceFragment,
} from './__generated-gql-types__/SelectATierModal.generated';

interface SelectATierModalHookProps {
  offeringPackage: SelectATierModalOfferingPackageFragment;
  onChoose: (tier: OfferingPackagePrice) => void;
  chosenTier: Nullable<OfferingPackagePrice>;
  isLoading: boolean;
}

interface SelectATierModalProps extends SelectATierModalHookProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const SelectATierModalComponent: FC<SelectATierModalProps> = ({
  open,
  setOpen,
  offeringPackage,
  onChoose,
  chosenTier,
  isLoading,
}) => {
  const tierOptions = offeringPackage.prices;

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalContent size={ModalSize.LARGE} useFullSpace>
        <div className="grid size-full auto-cols-fr grid-cols-2 sm:overflow-hidden">
          <div className="col-span-1 hidden bg-select-a-tier-modal bg-cover bg-fixed bg-center bg-no-repeat p-10 lg:block">
            {offeringPackage.isUniversalPackage ? null : (
              <>
                <ProfileImage
                  className="size-14"
                  {...offeringPackage.coach.user}
                />
              </>
            )}
            <h1 className="mt-6 text-2xl font-medium text-white">
              {offeringPackage.name}
            </h1>
            <div className="flex items-center justify-start space-x-2 text-lg font-normal text-white/80">
              {offeringPackage.isUniversalPackage ? null : (
                <p>Offered by {formatName(offeringPackage.coach.user)}</p>
              )}
              {offeringPackage.category ? (
                <>
                  {offeringPackage.isUniversalPackage ? null : (
                    <div className="size-0.5 rounded-full bg-white/80" />
                  )}
                  <p>{offeringPackage.category.name}</p>
                </>
              ) : null}
            </div>
            {offeringPackage.deliverables &&
            offeringPackage.deliverables.length > 0 ? (
              <div className="mt-12 flex flex-col items-start justify-start">
                <h3 className="text-sm font-medium uppercase text-leland-primary">
                  What you get from this package:
                </h3>
                {offeringPackage.deliverables.map((deliverable, index) => (
                  <div
                    key={index}
                    className="mt-6 flex items-start justify-start space-x-3 first:mt-0"
                  >
                    <SvgIcon
                      svgType={Svg.CHECK_CIRCLE_GREEN}
                      className="mt-0.5 size-5 shrink-0 text-white"
                    />
                    <p className="text-lg font-normal leading-6 text-white">
                      {deliverable}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <div className="col-span-2 p-6 sm:overflow-scroll lg:col-span-1 lg:px-8 lg:py-10">
            <h1 className="text-2xl font-medium text-leland-gray-dark">
              Select a{' '}
              {offeringPackage.isUniversalPackage ? 'package size' : 'tier'}
            </h1>
            <div className="mt-6">
              {tierOptions?.map(
                (tier) =>
                  offeringPackage.category && (
                    <Tier
                      key={`${tier.sku} - ${tier.tier}`}
                      className="mt-4"
                      tier={tier}
                      onChoose={onChoose}
                      chosenTier={chosenTier}
                      isLoading={isLoading}
                      goal={offeringPackage.category.goal}
                      isUniversalPackage={offeringPackage.isUniversalPackage}
                    />
                  ),
              )}
            </div>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

interface TierProps {
  tier: SelectATierModalOfferingPackagePriceFragment;
  onChoose: (tier: OfferingPackagePrice) => void;
  chosenTier: Nullable<OfferingPackagePrice>;
  isLoading: boolean;
  goal?: GoalName;
  className: string;
  isUniversalPackage: Possible<boolean>;
}

const Tier: FC<TierProps> = ({
  tier,
  onChoose,
  chosenTier,
  isLoading,
  goal,
  className,
  isUniversalPackage,
}) => {
  const [tierDescriptionLines, tierDescriptionRef] = useCountLines();

  const [expandedTierDescription, setExpandedTierDescription] = useState(false);

  const coachingTimeLabel = getOfferingPackageCoachingTimeLabel([tier]);

  const tierNameHumanReadable =
    isUniversalPackage && tier.tier
      ? humanizeTier(tier.tier)
      : (tier.sku ?? '');

  return (
    <div
      className={`flex flex-col rounded-lg border border-leland-gray-stroke p-6 shadow-sm ${className}`}
    >
      <div className="flex items-center justify-between space-x-4">
        <div className="flex flex-col items-start justify-center">
          <h3
            className="line-clamp-2 text-lg font-medium text-leland-gray-dark"
            title={tierNameHumanReadable}
          >
            {tierNameHumanReadable}
            {isUniversalPackage
              ? ` • ${
                  tier.packageCoachingTime?.exactTimeInMinute
                    ? formatTimeFromMinutes(
                        tier.packageCoachingTime.exactTimeInMinute,
                      )
                    : `Comprehensive${tier.sku ? ` - ${tier.sku}` : ''}`
                }`
              : null}
          </h3>
          {isUniversalPackage ? (
            <h4>{formatPrice(tier.price)}</h4>
          ) : (
            <div className="flex items-center justify-start space-x-2 text-base">
              <p className="font-medium text-leland-primary">
                {formatPrice(tier.price)}
              </p>
              {coachingTimeLabel ? (
                <>
                  <span>&middot;</span>
                  <p className="font-normal text-leland-gray-light">
                    {coachingTimeLabel}
                  </p>
                </>
              ) : null}
            </div>
          )}
        </div>
        <div className="hidden lg:block">
          <Button
            label="Choose"
            buttonColor={ButtonColor.PRIMARY}
            type="button"
            onClick={() => onChoose(tier)}
            disabled={!!(isLoading && tier.sku === chosenTier)}
          />
        </div>
      </div>
      {isUniversalPackage && tier.tier ? (
        <div className="mt-5 border-t border-leland-gray-stroke pt-5 text-sm text-leland-gray-light">
          {getTierDescription(tier.tier, goal)}
        </div>
      ) : tier.description ? (
        <>
          <hr className="my-4" />
          <div className="flex flex-col items-start justify-center">
            <p
              ref={tierDescriptionRef}
              className={`whitespace-pre-line break-words text-base font-normal text-leland-gray-light ${
                !expandedTierDescription &&
                tierDescriptionLines > 2 &&
                'line-clamp-2'
              }`}
            >
              {tier.description}
            </p>
            {tierDescriptionLines > 2 ? (
              <button
                className="link link--primary mt-0.5 flex items-center justify-start space-x-1.5 text-base font-normal"
                onClick={() =>
                  setExpandedTierDescription((prevState) => !prevState)
                }
              >
                <p>{expandedTierDescription ? 'Read less' : 'Read more'}</p>
                <IconChevronDown
                  className={`size-2.5 text-leland-primary transition-transform ${
                    expandedTierDescription ? 'rotate-180' : ''
                  }`}
                />
              </button>
            ) : null}
          </div>
        </>
      ) : null}
      {/* MOBILE */}
      <div className="mt-4 block lg:hidden">
        <Button
          label="Choose"
          type="button"
          buttonColor={ButtonColor.PRIMARY}
          onClick={() => onChoose(tier)}
          disabled={
            !!(
              isLoading &&
              (tier.sku === chosenTier?.sku || tier.tier === chosenTier?.tier)
            )
          }
        />
      </div>
    </div>
  );
};

export const useSelectATierModal: ModalHook<SelectATierModalHookProps> = () => {
  const [open, setOpen] = useControlledModalState();
  const SelectATierModal: FC<SelectATierModalHookProps> = (props) =>
    open ? (
      <SelectATierModalComponent {...props} open={open} setOpen={setOpen} />
    ) : null;
  return { open, setOpen, Modal: SelectATierModal };
};
